/**
 * Synced collection state (part of ProgressState as `collect`): stickers stuck in the album, creature
 * cards already opened, the gallery-wall frame of each drawing, the weekly rhythm goal and the last
 * "Today's drawing" pick (so the server's reminder can name the same drawing the app shows).
 *
 * Merge rules (`mergeCollection`, commutative + idempotent + associative):
 *  - `placed`, `opened`: set union (sorted, so both orders give identical output).
 *  - `frames`: per journal id, last write wins by `at` (ISO time); equal `at` → the larger frame id.
 *  - `goal`: last write wins by `at`; equal `at` → the larger value; `null` (= default) loses to any value.
 *  - `today`: the later `day` wins; same day → the larger drawing id.
 *
 * Legacy: before this field existed, the client kept the same data local-only in localStorage
 * `sketchling-collect-v1` (zustand persist: `{ state: { byProfile: { [profileId]: { placed, opened,
 * frames, goal } } }, version }`). `foldLegacyCollect` merges it into each profile's progress; legacy
 * frames / goal are stamped with LEGACY_AT (epoch) so they never override a value set since.
 */
import { DEFAULT_WEEKLY_GOAL } from './habit';

/** A value with the time it was set (ISO), for last-write-wins merging. */
export interface Stamped<T> {
  v: T;
  at: string;
}

export interface CollectionState {
  /** Album sticker ids already peeled and stuck in. */
  placed: string[];
  /** Creature cards (drawing ids) opened at least once (clears the "New!" badge). */
  opened: string[];
  /** Gallery wall frame id per journal item id. */
  frames: Record<string, Stamped<string>>;
  /** Weekly rhythm goal (drawings per week); null = DEFAULT_WEEKLY_GOAL. */
  goal: Stamped<number> | null;
  /** Last "Today's drawing" pick shown in the app (local day YYYY-MM-DD). */
  today: { day: string; drawingId: string } | null;
  /** Story layer: cast members met (`world:castId`, Character Cards in the "Residents" tab). Absent = none. */
  met?: string[];
  /** Per-profile preferences (synced, last write wins each). Absent = defaults. */
  prefs?: ProfilePrefs;
}

/**
 * Per-profile preferences that follow the child to every device:
 *  - `storyMode`: "Story before lessons" (always / first time only / off), default first time only;
 *  - `storyNarration`: the characters narrate the drawing steps (default on);
 *  - `storySkips`: "Draw now" picks in a row (≥ 3 → the meet card stops offering itself; "Hear the story" resets);
 *  - `locale`: the app language chosen for this child (BCP 47 id from core i18n, e.g. `zh-Hans`).
 */
export interface ProfilePrefs {
  storyMode?: Stamped<'always' | 'first' | 'off'>;
  storyNarration?: Stamped<boolean>;
  storySkips?: Stamped<number>;
  locale?: Stamped<string>;
}
const PREF_KEYS = ['storyMode', 'storyNarration', 'storySkips', 'locale'] as const;
const validPref: Record<(typeof PREF_KEYS)[number], (v: unknown) => boolean> = {
  storyMode: (v) => v === 'always' || v === 'first' || v === 'off',
  storyNarration: (v) => typeof v === 'boolean',
  storySkips: (v) => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 99,
  locale: (v) => typeof v === 'string' && /^[a-z]{2,3}(-[A-Za-z]{2,4})?$/.test(v),
};
function normalizePrefs(raw: unknown): ProfilePrefs | undefined {
  if (!isObj(raw)) return undefined;
  const out: Record<string, Stamped<unknown>> = {};
  for (const k of PREF_KEYS) {
    const p = raw[k];
    if (stamped(p) && validPref[k](p.v)) out[k] = { v: p.v, at: p.at };
  }
  return Object.keys(out).length ? (out as ProfilePrefs) : undefined;
}
function mergePrefs(a: ProfilePrefs | undefined, b: ProfilePrefs | undefined): ProfilePrefs | undefined {
  if (!a) return b;
  if (!b) return a;
  const out: Record<string, Stamped<unknown>> = {};
  for (const k of PREF_KEYS) {
    const v = lww<unknown>(a[k] as Stamped<unknown> | undefined, b[k] as Stamped<unknown> | undefined, (x, y) => String(x) > String(y));
    if (v) out[k] = v;
  }
  return out as ProfilePrefs;
}

/** Set one per-profile preference (same object when unchanged). */
export function setProfilePref<K extends keyof ProfilePrefs>(c: CollectionState, key: K, v: NonNullable<ProfilePrefs[K]>['v'], at: string): CollectionState {
  if (c.prefs?.[key]?.v === v) return c;
  return { ...c, prefs: { ...c.prefs, [key]: { v, at } } };
}

export const LEGACY_AT = new Date(0).toISOString();
export const LEGACY_COLLECT_KEY = 'sketchling-collect-v1';

export function emptyCollection(): CollectionState {
  return { placed: [], opened: [], frames: {}, goal: null, today: null };
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const strings = (v: unknown): string[] => (Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === 'string' && !!x))].sort() : []);
const validGoal = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= 7;
const stamped = (v: unknown): v is Stamped<unknown> => isObj(v) && typeof v.at === 'string';

/** Repair any (possibly foreign / older) value into a valid CollectionState. */
export function normalizeCollection(raw: unknown): CollectionState {
  if (!isObj(raw)) return emptyCollection();
  const frames: Record<string, Stamped<string>> = {};
  if (isObj(raw.frames)) {
    for (const [k, f] of Object.entries(raw.frames)) if (stamped(f) && typeof f.v === 'string' && f.v) frames[k] = { v: f.v, at: f.at };
  }
  const goal = stamped(raw.goal) && validGoal(raw.goal.v) ? { v: raw.goal.v, at: raw.goal.at } : null;
  const t = raw.today;
  const today = isObj(t) && typeof t.day === 'string' && typeof t.drawingId === 'string' && t.drawingId ? { day: t.day, drawingId: t.drawingId } : null;
  const met = strings(raw.met);
  const prefs = normalizePrefs(raw.prefs);
  return { placed: strings(raw.placed), opened: strings(raw.opened), frames, goal, today, ...(met.length ? { met } : {}), ...(prefs ? { prefs } : {}) };
}

/** Later `at` wins; tie → `tie(a, b)` picks deterministically. */
function lww<T>(a: Stamped<T> | null | undefined, b: Stamped<T> | null | undefined, larger: (x: T, y: T) => boolean): Stamped<T> | null {
  if (!a) return b ?? null;
  if (!b) return a;
  if (a.at !== b.at) return a.at > b.at ? a : b;
  return larger(b.v, a.v) ? b : a;
}

export function mergeCollection(aIn: unknown, bIn: unknown): CollectionState {
  const a = normalizeCollection(aIn);
  const b = normalizeCollection(bIn);
  const frames: Record<string, Stamped<string>> = {};
  for (const k of [...new Set([...Object.keys(a.frames), ...Object.keys(b.frames)])].sort()) {
    frames[k] = lww(a.frames[k], b.frames[k], (x, y) => x > y)!;
  }
  const today =
    !a.today || !b.today
      ? (a.today ?? b.today)
      : a.today.day !== b.today.day
        ? a.today.day > b.today.day
          ? a.today
          : b.today
        : a.today.drawingId >= b.today.drawingId
          ? a.today
          : b.today;
  return {
    placed: [...new Set([...a.placed, ...b.placed])].sort(),
    opened: [...new Set([...a.opened, ...b.opened])].sort(),
    frames,
    goal: lww(a.goal, b.goal, (x, y) => x > y),
    today: today ? { ...today } : null,
    ...(a.met?.length || b.met?.length ? { met: [...new Set([...(a.met ?? []), ...(b.met ?? [])])].sort() } : {}),
    ...(a.prefs || b.prefs ? { prefs: mergePrefs(a.prefs, b.prefs) } : {}),
  };
}

/** Story layer: remember that the child met these cast members (grow-only, `world:castId`). */
export function meetCharacters(c: CollectionState, keys: string[]): CollectionState {
  const met = [...new Set([...(c.met ?? []), ...keys])].sort();
  return met.length === (c.met?.length ?? 0) ? c : { ...c, met };
}

/** Effective weekly goal. */
export const collectionGoal = (c: CollectionState | null | undefined): number => (c?.goal && validGoal(c.goal.v) ? c.goal.v : DEFAULT_WEEKLY_GOAL);

/** Plain frame map (journal id → frame id). */
export function collectionFrames(c: CollectionState): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, f] of Object.entries(c.frames)) out[k] = f.v;
  return out;
}

/* ------------------------------------ Updates ------------------------------------ */

const addAll = (list: string[], ids: readonly string[]) => {
  const add = ids.filter((x) => typeof x === 'string' && x && !list.includes(x));
  return add.length ? [...new Set([...list, ...add])].sort() : list;
};

/** Stick stickers in the album (returns the same object when nothing changes). */
export function placeStickers(c: CollectionState, ids: readonly string[]): CollectionState {
  const placed = addAll(c.placed, ids);
  return placed === c.placed ? c : { ...c, placed };
}

/** Mark creature cards as opened (same object when nothing changes). */
export function openCards(c: CollectionState, drawingIds: readonly string[]): CollectionState {
  const opened = addAll(c.opened, drawingIds);
  return opened === c.opened ? c : { ...c, opened };
}

export function setCollectionFrame(c: CollectionState, journalId: string, frame: string, at: string): CollectionState {
  if (c.frames[journalId]?.v === frame) return c;
  return { ...c, frames: { ...c.frames, [journalId]: { v: frame, at } } };
}

export function setCollectionGoal(c: CollectionState, goal: number, at: string): CollectionState {
  if (!validGoal(goal) || c.goal?.v === goal) return c;
  return { ...c, goal: { v: goal, at } };
}

export function setTodayPick(c: CollectionState, day: string, drawingId: string): CollectionState {
  if (c.today?.day === day && c.today.drawingId === drawingId) return c;
  return { ...c, today: { day, drawingId } };
}

/* ------------------------------------ Legacy ------------------------------------ */

/** Per-profile collections from the legacy `sketchling-collect-v1` JSON (string or parsed); {} when unreadable. */
export function legacyCollections(raw: unknown): Record<string, CollectionState> {
  let v: unknown = raw;
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v);
    } catch {
      return {};
    }
  }
  const byProfile = isObj(v) && isObj(v.state) ? v.state.byProfile : isObj(v) ? v.byProfile : null;
  if (!isObj(byProfile)) return {};
  const out: Record<string, CollectionState> = {};
  for (const [pid, p] of Object.entries(byProfile)) {
    if (!isObj(p)) continue;
    const frames: Record<string, Stamped<string>> = {};
    if (isObj(p.frames)) for (const [k, f] of Object.entries(p.frames)) if (typeof f === 'string' && f) frames[k] = { v: f, at: LEGACY_AT };
    out[pid] = normalizeCollection({
      placed: p.placed,
      opened: p.opened,
      frames,
      goal: validGoal(p.goal) && p.goal !== DEFAULT_WEEKLY_GOAL ? { v: p.goal, at: LEGACY_AT } : null,
      today: null,
    });
  }
  return out;
}
